import type { MatrixData } from '../components/MatrixTable'
import type { ProductionSpeciesKey } from '../data/productionSpeciesPresets'
import type { ProductionUnit } from '../types/farm'
import type { WizardFarm, WizardProductionUnit } from '../types/farmWizard'
import type { Lot } from '../types/lot'

/**
 * Generic facility/production-unit/lot profile store for a species-keyed
 * matrix (SFR, mortality, ...), factored out of the original SFR-only
 * `sfrProfiles.ts` so a second matrix domain doesn't have to duplicate this
 * ~250 lines of CRUD + resolution logic. Everything is stored as JSON inside
 * the existing `Farm.metadata`/`ProductionUnit.metadata`/`Lot.metadata`
 * blobs -- no schema, no migration, no API change -- keyed by the storage
 * keys the caller supplies so two domains never collide on the same farm.
 */

export type MatrixProfileRecord = {
  id: string
  species: ProductionSpeciesKey
  name: string
  matrix: MatrixData
  createdAt: string
  updatedAt: string
}

export type ResolvedMatrixProfileSelection = {
  source: 'lot' | 'production-unit' | 'facility-default' | 'species-default'
  profile: MatrixProfileRecord | null
  profileId: string | null
}

export type MatrixProfileStoreKeys = {
  profilesKey: string
  defaultAssignmentsKey: string
  assignmentsKey: string
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
)

const isMatrixData = (value: unknown): value is MatrixData => {
  if (!isRecord(value)) return false

  return Array.isArray(value.temperatures)
    && Array.isArray(value.weights)
    && Array.isArray(value.values)
}

const isProductionSpeciesKey = (value: unknown): value is ProductionSpeciesKey => (
  value === 'trout' || value === 'salmon' || value === 'tilapia' || value === 'shrimp'
)

const cloneMatrix = (matrix: MatrixData): MatrixData => ({
  temperatures: [...matrix.temperatures],
  weights: [...matrix.weights],
  values: matrix.values.map((row) => [...row])
})

const cloneMetadata = (value?: Record<string, unknown>): Record<string, unknown> => (value ? { ...value } : {})

const cloneProfile = (profile: MatrixProfileRecord): MatrixProfileRecord => ({
  ...profile,
  matrix: cloneMatrix(profile.matrix)
})

const normalizeProfile = (value: unknown): MatrixProfileRecord | null => {
  if (!isRecord(value)) return null
  if (typeof value.id !== 'string' || !value.id.trim()) return null
  if (!isProductionSpeciesKey(value.species)) return null
  if (typeof value.name !== 'string' || !value.name.trim()) return null
  if (!isMatrixData(value.matrix)) return null

  return {
    id: value.id.trim(),
    species: value.species,
    name: value.name.trim(),
    matrix: cloneMatrix(value.matrix),
    createdAt: typeof value.createdAt === 'string' && value.createdAt.trim() ? value.createdAt : new Date(0).toISOString(),
    updatedAt: typeof value.updatedAt === 'string' && value.updatedAt.trim() ? value.updatedAt : new Date(0).toISOString()
  }
}

export const createMatrixProfileStore = (
  keys: MatrixProfileStoreKeys,
  getDefaultMatrixForSpecies: (species: string) => MatrixData
) => {
  const readProfilesFromMetadata = (farm: WizardFarm | undefined): MatrixProfileRecord[] => {
    const raw = farm?.metadata?.[keys.profilesKey]
    if (!Array.isArray(raw)) return []

    return raw
      .map((entry) => normalizeProfile(entry))
      .filter((entry): entry is MatrixProfileRecord => Boolean(entry))
  }

  const readAssignmentsMap = (
    metadata: Record<string, unknown> | undefined,
    key: string
  ): Partial<Record<ProductionSpeciesKey, string>> => {
    const raw = metadata?.[key]
    if (!isRecord(raw)) return {}

    const entries = Object.entries(raw).flatMap(([species, profileId]) => {
      if (!isProductionSpeciesKey(species)) return []
      if (typeof profileId !== 'string') return []

      const normalizedProfileId = profileId.trim()
      if (!normalizedProfileId) return []

      return [[species, normalizedProfileId] as const]
    })

    return Object.fromEntries(entries) as Partial<Record<ProductionSpeciesKey, string>>
  }

  const writeAssignmentsMap = (
    metadata: Record<string, unknown>,
    key: string,
    nextAssignments: Partial<Record<ProductionSpeciesKey, string>>
  ): void => {
    const entries = Object.entries(nextAssignments).filter(([, profileId]) => typeof profileId === 'string' && profileId.trim())
    if (entries.length === 0) {
      delete metadata[key]
      return
    }

    metadata[key] = Object.fromEntries(entries)
  }

  const listFarmProfiles = (
    farm: WizardFarm | undefined,
    species?: ProductionSpeciesKey
  ): MatrixProfileRecord[] => {
    const profiles = readProfilesFromMetadata(farm)
    const scoped = species ? profiles.filter((profile) => profile.species === species) : profiles
    return scoped
      .slice()
      .sort((left, right) => left.name.localeCompare(right.name))
      .map((profile) => cloneProfile(profile))
  }

  const findFarmProfile = (
    farm: WizardFarm | undefined,
    profileId: string | null | undefined
  ): MatrixProfileRecord | null => {
    const normalizedId = typeof profileId === 'string' ? profileId.trim() : ''
    if (!normalizedId) return null

    const profile = readProfilesFromMetadata(farm).find((entry) => entry.id === normalizedId)
    return profile ? cloneProfile(profile) : null
  }

  const readFarmDefaultProfileId = (
    farm: WizardFarm | undefined,
    species: ProductionSpeciesKey
  ): string | null => {
    const assignments = readAssignmentsMap(farm?.metadata, keys.defaultAssignmentsKey)
    return assignments[species] ?? null
  }

  const isFarmDefaultProfile = (
    farm: WizardFarm | undefined,
    species: ProductionSpeciesKey,
    profileId: string | null | undefined
  ): boolean => {
    const normalizedId = typeof profileId === 'string' ? profileId.trim() : ''
    if (!normalizedId) return false
    return readFarmDefaultProfileId(farm, species) === normalizedId
  }

  const upsertFarmProfile = (
    farm: WizardFarm,
    profile: MatrixProfileRecord
  ): WizardFarm => {
    const nextProfiles = readProfilesFromMetadata(farm)
    const nextIndex = nextProfiles.findIndex((entry) => entry.id === profile.id)
    const cloned = cloneProfile(profile)

    if (nextIndex >= 0) {
      nextProfiles[nextIndex] = cloned
    } else {
      nextProfiles.push(cloned)
    }

    const metadata = cloneMetadata(farm.metadata)
    metadata[keys.profilesKey] = nextProfiles.map((entry) => cloneProfile(entry))

    return {
      ...farm,
      metadata
    }
  }

  const removeFarmProfile = (
    farm: WizardFarm,
    profileId: string
  ): WizardFarm => {
    const normalizedId = profileId.trim()
    const nextProfiles = readProfilesFromMetadata(farm).filter((profile) => profile.id !== normalizedId)
    const metadata = cloneMetadata(farm.metadata)

    if (nextProfiles.length === 0) {
      delete metadata[keys.profilesKey]
    } else {
      metadata[keys.profilesKey] = nextProfiles.map((profile) => cloneProfile(profile))
    }

    const nextDefaults = readAssignmentsMap(metadata, keys.defaultAssignmentsKey)
    for (const species of Object.keys(nextDefaults) as ProductionSpeciesKey[]) {
      if (nextDefaults[species] === normalizedId) {
        delete nextDefaults[species]
      }
    }
    writeAssignmentsMap(metadata, keys.defaultAssignmentsKey, nextDefaults)

    return {
      ...farm,
      metadata
    }
  }

  const setFarmDefaultProfileId = (
    farm: WizardFarm,
    species: ProductionSpeciesKey,
    profileId: string | null | undefined
  ): WizardFarm => {
    const metadata = cloneMetadata(farm.metadata)
    const nextDefaults = readAssignmentsMap(metadata, keys.defaultAssignmentsKey)
    const normalizedId = typeof profileId === 'string' ? profileId.trim() : ''

    if (normalizedId) {
      nextDefaults[species] = normalizedId
    } else {
      delete nextDefaults[species]
    }

    writeAssignmentsMap(metadata, keys.defaultAssignmentsKey, nextDefaults)

    return {
      ...farm,
      metadata
    }
  }

  const readAssignedProfileId = (
    target: Pick<ProductionUnit, 'metadata'> | Pick<Lot, 'metadata'> | undefined,
    species: ProductionSpeciesKey
  ): string | null => {
    const assignments = readAssignmentsMap(target?.metadata, keys.assignmentsKey)
    return assignments[species] ?? null
  }

  const setProductionUnitProfileAssignment = (
    unit: WizardProductionUnit,
    species: ProductionSpeciesKey,
    profileId: string | null | undefined
  ): WizardProductionUnit => {
    const metadata = cloneMetadata(unit.metadata)
    const nextAssignments = readAssignmentsMap(metadata, keys.assignmentsKey)
    const normalizedId = typeof profileId === 'string' ? profileId.trim() : ''

    if (normalizedId) {
      nextAssignments[species] = normalizedId
    } else {
      delete nextAssignments[species]
    }

    writeAssignmentsMap(metadata, keys.assignmentsKey, nextAssignments)

    return {
      ...unit,
      metadata
    }
  }

  const setLotProfileAssignment = (
    lot: Lot,
    species: ProductionSpeciesKey,
    profileId: string | null | undefined
  ): Lot => {
    const metadata = cloneMetadata(lot.metadata)
    const nextAssignments = readAssignmentsMap(metadata, keys.assignmentsKey)
    const normalizedId = typeof profileId === 'string' ? profileId.trim() : ''

    if (normalizedId) {
      nextAssignments[species] = normalizedId
    } else {
      delete nextAssignments[species]
    }

    writeAssignmentsMap(metadata, keys.assignmentsKey, nextAssignments)

    return {
      ...lot,
      metadata
    }
  }

  const resolveProfileSelection = (options: {
    farm?: WizardFarm
    productionUnit?: ProductionUnit
    lot?: Lot
    species: ProductionSpeciesKey
  }): ResolvedMatrixProfileSelection => {
    const lotProfileId = readAssignedProfileId(options.lot, options.species)
    const lotProfile = findFarmProfile(options.farm, lotProfileId)
    if (lotProfile) {
      return {
        source: 'lot',
        profile: lotProfile,
        profileId: lotProfile.id
      }
    }

    const productionUnitProfileId = readAssignedProfileId(options.productionUnit, options.species)
    const productionUnitProfile = findFarmProfile(options.farm, productionUnitProfileId)
    if (productionUnitProfile) {
      return {
        source: 'production-unit',
        profile: productionUnitProfile,
        profileId: productionUnitProfile.id
      }
    }

    const facilityDefaultProfileId = readFarmDefaultProfileId(options.farm, options.species)
    const facilityDefaultProfile = findFarmProfile(options.farm, facilityDefaultProfileId)
    if (facilityDefaultProfile) {
      return {
        source: 'facility-default',
        profile: facilityDefaultProfile,
        profileId: facilityDefaultProfile.id
      }
    }

    return {
      source: 'species-default',
      profile: null,
      profileId: null
    }
  }

  const resolveMatrixForSelection = (
    selection: ResolvedMatrixProfileSelection,
    species: ProductionSpeciesKey,
    speciesDefaultMatrix?: MatrixData | null
  ): MatrixData => {
    if (selection.profile) {
      return cloneProfile(selection.profile).matrix
    }

    if (speciesDefaultMatrix) {
      return cloneMatrix(speciesDefaultMatrix)
    }

    return getDefaultMatrixForSpecies(species)
  }

  const resolveEffectiveMatrix = (options: {
    farm?: WizardFarm
    productionUnit?: ProductionUnit
    lot?: Lot
    species: ProductionSpeciesKey
    speciesDefaultMatrix?: MatrixData | null
  }): MatrixData => {
    const selection = resolveProfileSelection(options)
    return resolveMatrixForSelection(selection, options.species, options.speciesDefaultMatrix)
  }

  return {
    listFarmProfiles,
    findFarmProfile,
    readFarmDefaultProfileId,
    isFarmDefaultProfile,
    upsertFarmProfile,
    removeFarmProfile,
    setFarmDefaultProfileId,
    readAssignedProfileId,
    setProductionUnitProfileAssignment,
    setLotProfileAssignment,
    resolveProfileSelection,
    resolveMatrixForSelection,
    resolveEffectiveMatrix
  }
}
